import type { LatencySample, NetworkLatencyStatus } from '../types';

export const LATENCY_WINDOW_MS = 60_000;
export const LATENCY_CAP_MS = 10_000;
export const MIN_SAMPLES_FOR_AVERAGE = 2;

export const THRESHOLD_GOOD_MS = 150;
export const THRESHOLD_WARNING_MS = 300;

/**
 * Keeps samples whose timestamps fall within the rolling window ending at `now`.
 */
export function trimLatencyWindow(
  samples: LatencySample[],
  now: number,
): LatencySample[] {
  return samples.filter((s) => now - s.timestampMs <= LATENCY_WINDOW_MS);
}

/**
 * Linear time decay: newest sample weight → 1, oldest at window edge → 0.
 * Individual latencies are capped at {@link LATENCY_CAP_MS} for the average only.
 */
export function computeWeightedAverageLatencyMs(
  samples: LatencySample[],
  now: number,
): number | null {
  if (samples.length < MIN_SAMPLES_FOR_AVERAGE) {
    return null;
  }

  let weightSum = 0;
  let weightedLatencySum = 0;

  for (const s of samples) {
    const ageMs = now - s.timestampMs;
    const weight = Math.max(0, (LATENCY_WINDOW_MS - ageMs) / LATENCY_WINDOW_MS);
    const capped = Math.min(s.latencyMs, LATENCY_CAP_MS);
    weightSum += weight;
    weightedLatencySum += weight * capped;
  }

  if (weightSum === 0) {
    return null;
  }

  return weightedLatencySum / weightSum;
}

export function latencyAverageToStatus(
  averageMs: number | null,
): NetworkLatencyStatus {
  if (averageMs === null) {
    return 'good';
  }
  if (averageMs > THRESHOLD_WARNING_MS) {
    return 'critical';
  }
  if (averageMs >= THRESHOLD_GOOD_MS) {
    return 'warning';
  }
  return 'good';
}
